import { Event } from '@sentry/types';

/**
 * Filters out Statsig initialization errors that occur when users don't accept cookies.
 * This is expected behavior and not a fixable error, so we ignore it in Sentry
 * to prevent it from consuming 40% of our Sentry quota (EB-314800).
 *
 * When users don't accept cookies, Statsig initialization can fail with timeout errors
 * because Statsig API requests are blocked by cookie consent managers.
 *
 * @param event - Sentry event to check
 * @returns true if the error should be ignored, false otherwise
 */
export const shouldIgnoreStatsigError = (event: Event): boolean => {
    const errorText =
        event.message || event.exception?.values?.[0]?.value || '';

    // Only ignore Statsig initialization errors
    if (!errorText.includes('Statsig initialization failed')) {
        return false;
    }

    // Ignore initialization timeout errors - these occur when cookie consent blocks network requests
    // Example: "Statsig initialization failed after 3025ms: The initialization timeout of 3000ms has been hit before the network request has completed."
    const isInitializationTimeoutError =
        /initialization timeout.*has been hit.*before.*network.*request.*completed/i.test(
            errorText,
        ) || /initialization timeout.*network/i.test(errorText);

    return isInitializationTimeoutError;
};
